import { Fragment } from 'react';
import { Table, Edit3, Layers } from 'lucide-react';
import { useUiLocale } from '../i18n/useUiLocale';

/** Pick purpose text for the device language. */
export function tablePurpose(t, locale) {
  if (locale === 'ko' && t?.description_ko && t.description_ko !== '-') {
    return t.description_ko;
  }
  if (t?.description && t.description !== '-') return t.description;
  return null;
}

/**
 * Read-only / editable catalogue of MySQL tables (name + purpose).
 * Used on Settings Overview and Settings → DB Setting.
 */
export default function DbTableCatalogue({
  tables,
  loading,
  filter,
  onFilterChange,
  unlocked = false,
  onEditData,
  onEditStructure,
  title,
  subtitle,
}) {
  const locale = useUiLocale();
  const heading = title || (locale === 'ko' ? '데이터베이스 테이블' : 'Database tables');
  const purposeLabel = locale === 'ko' ? '설명' : 'Purpose';
  const tableLabel = locale === 'ko' ? '테이블' : 'Table';
  const filterPh =
    locale === 'ko' ? '이름 또는 설명으로 검색…' : 'Filter by name or purpose…';
  const emptyMsg = locale === 'ko' ? '검색 결과가 없습니다.' : 'No tables match this filter.';
  const noDesc = locale === 'ko' ? '설명 없음' : 'No description yet';

  const grouped = (() => {
    const map = new Map();
    for (const t of tables || []) {
      const cat = t.category || 'Other';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(t);
    }
    return [...map.entries()];
  })();

  return (
    <div className="bg-card rounded-lg shadow border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b bg-slate-50 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{heading}</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {loading
              ? locale === 'ko'
                ? '불러오는 중…'
                : 'Loading…'
              : subtitle ||
                (locale === 'ko'
                  ? `${tables.length}개 테이블 · 이름 + 기본 설명`
                  : `${tables.length} table${tables.length === 1 ? '' : 's'} · name + purpose`)}
          </p>
        </div>
        {onFilterChange ? (
          <input
            type="search"
            value={filter || ''}
            onChange={(e) => onFilterChange(e.target.value)}
            placeholder={filterPh}
            className="w-full sm:w-72 px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
          />
        ) : null}
      </div>
      {loading ? (
        <p className="px-4 py-8 text-sm text-gray-500">
          {locale === 'ko' ? '테이블 목록을 불러오는 중…' : 'Loading table list…'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-left text-gray-600">
              <tr>
                <th className="px-4 py-2 font-medium w-[28%]">{tableLabel}</th>
                <th className="px-4 py-2 font-medium">{purposeLabel}</th>
                {unlocked ? (
                  <th className="px-4 py-2 font-medium text-right w-56">
                    {locale === 'ko' ? '작업' : 'Actions'}
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {grouped.map(([category, rows]) => (
                <Fragment key={`cat-${category}`}>
                  <tr className="bg-slate-100/90">
                    <td
                      colSpan={unlocked ? 3 : 2}
                      className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600"
                    >
                      {category}
                    </td>
                  </tr>
                  {rows.map((t) => {
                    const purpose = tablePurpose(t, locale);
                    return (
                      <tr key={t.name} className="border-t hover:bg-slate-50/80">
                        <td className="px-4 py-2.5 align-top">
                          <div className="flex items-start gap-2">
                            <Table className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                            <span className="font-mono text-[13px] font-semibold text-gray-900">
                              {t.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 align-top">
                          {purpose || <span className="text-amber-700">{noDesc}</span>}
                        </td>
                        {unlocked ? (
                          <td className="px-4 py-2.5 text-right align-top whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => onEditData?.(t)}
                              className="inline-flex items-center px-2.5 py-1 mr-1.5 bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                            >
                              <Edit3 className="w-3.5 h-3.5 mr-1" />
                              Data
                            </button>
                            <button
                              type="button"
                              onClick={() => onEditStructure?.(t)}
                              className="inline-flex items-center px-2.5 py-1 bg-gray-100 text-gray-800 rounded hover:bg-gray-200"
                            >
                              <Layers className="w-3.5 h-3.5 mr-1" />
                              Structure
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
              {!tables.length ? (
                <tr>
                  <td colSpan={unlocked ? 3 : 2} className="px-4 py-8 text-center text-gray-500">
                    {emptyMsg}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
